//sectorusecases.js
import { useState, useEffect, useMemo } from "react";
import {
  Table,
  Checkbox,
  Button,
  Modal,
  Breadcrumb,
  Tag,
  Divider,
  message,
  Card,
  Space,
  Typography,
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ShieldCheck, CheckCircle2
} from "lucide-react";
import Header from "../header/header2";
import axios from "../../utils/axios";
import useCasesConfig from "../../constants/useCases";
import { CrownOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const SectorUseCases = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem("user"));
  const citriot = JSON.parse(localStorage.getItem("citriot"));
  const organizationId = localStorage.getItem("organizationId");
  const mainCategory = location.state?.mainCategory || localStorage.getItem("mainCategory") 
  const projectname =
    location.state?.projectname ||
    localStorage.getItem("projectname") ||
    "";

  const [selectedData, setSelectedData] = useState({});
  const [userUseCases, setUserUseCases] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    localStorage.setItem("mainCategory", mainCategory);
  }, [mainCategory]);

  const useCases = useCasesConfig[mainCategory] || {};

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const username = citriot?.auth?.user;

        if (!username || !organizationId) return;

        const response = await axios.post("/fetch-user-data", {
          username,
          organizationId,
          projectname
        });

        if (response.data.success) {
          if (response.data.source === "project") {
            // 🔥 Convert project selections → selectedData
            const formatted = {};

            response.data.selections.forEach((cat) => {
              if (!formatted[cat.category]) {
                formatted[cat.category] = {};
              }

              cat.useCases.forEach((uc) => {
                formatted[cat.category][uc.id] =
                  uc.selectedFunctionalities || [];
              });
            });

            setSelectedData(formatted);

          } else {
            const userCases = response.data.useCases || [];

            const formatted = {};

            userCases.forEach((uc) => {
              // 🔥 normalize id (VERY IMPORTANT)
              const normalizedId = uc.id.toLowerCase().replace(/\s+/g, "-");

              // find category from config
              Object.entries(useCasesConfig[response.data.sector] || {}).forEach(
                ([category, items]) => {
                  const match = items.find(
                    (item) => item.id === normalizedId
                  );

                  if (match) {
                    if (!formatted[category]) {
                      formatted[category] = {};
                    }

                    formatted[category][normalizedId] =
                      uc.functionalities || [];
                  }
                }
              );
            });

            setSelectedData(formatted);
          }
        }
      } catch (err) {
        console.error("Fetch user usecases failed:", err);
      }
    };

    fetchUserData();
  }, [mainCategory, projectname]);

  useEffect(() => {
    if (!mainCategory) return;
    setSelectedData({});
  }, [mainCategory]);

  // Filter use cases based on search text
  const filteredUseCases = useMemo(() => {
    if (!searchText.trim()) return useCases;

    const query = searchText.toLowerCase().trim();
    const words = query.split(/\s+/).filter(Boolean);

    return Object.fromEntries(
      Object.entries(useCases ||{})
        .map(([category, items]) => {
          const matchingItems = items.filter((useCase) =>
            words.some(
              (word) =>
                useCase.title.toLowerCase().includes(word) ||
                useCase.functionalities.some((f) =>
                  f.toLowerCase().includes(word)
                ) ||
                category.toLowerCase().includes(word)
            )
          );
          return [category, matchingItems];
        })
        .filter(([, items]) => items.length > 0)
    );
  }, [searchText]);


  // Build table data from filtered use cases
  const tableData = Object.keys(filteredUseCases).map((cat) => ({
  key: cat,
  category: cat,
  count: filteredUseCases[cat].length,
}));



  const onFunctionalityChange = (category, useCaseId, values) => {
    const previous = selectedData?.[category]?.[useCaseId] || [];

    const isAdding = values.length > previous.length;

    // 🔥 ONLY consider current category (IMPORTANT FIX)
    const currentCategoryData = selectedData?.[category] || {};

    const totalWithoutCurrent = Object.entries(currentCategoryData)
      .filter(([id]) => id !== useCaseId)
      .flatMap(([, funcs]) => funcs)
      .length;

    const newTotal = totalWithoutCurrent + values.length;

    if (isAdding && newTotal > 4) {
      message.warning(
        "You can select a maximum of four use cases. Please deselect an existing option to continue."
      );
      return;
    }

    // ✅ OPTIONAL: clear other categories when switching
    setSelectedData({
      [category]: {
        ...currentCategoryData,
        [useCaseId]: values,
      },
    });
  };

  const selectedCount = Object.values(selectedData)
    .flatMap((cat) => Object.values(cat || {}))
    .flat().length;

  const fetchProjectProcess = async () => {
    try {
      const response = await axios.post("/check-project-process", {
        username: user.username,
        projectName: projectname,
        organizationId: organizationId
      });

      const { selectCamera, addcamera } = response.data;

      if (!selectCamera) {
        navigate("/selectcamera", {
          state: { projectname }
        });
        return;
      }

      if (!addcamera) {
        navigate("/add-camera", {
          state: { projectname }
        });
        return;
      }

      navigate("/selectmodel", {
        state: { projectname }
      });

    } catch (error) {
      console.error(error);
      message.error("Failed to check project status");
    }
  };

  const handleContinue = () => {
    if (selectedCount === 0) {
      message.warning("Please select at least one feature");
      return;
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const selections = Object.entries(selectedData)
        .map(([category, cases]) => {
          const useCasesPayload = Object.entries(cases)
            .filter(([, funcs]) => funcs.length)
            .map(([id, funcs]) => {
              const uc = useCases?.[category]?.find?.(u => u.id === id);
              return {
                id,
                title: uc?.title || id,
                selectedFunctionalities: funcs
              };
            });

          return useCasesPayload.length
            ? { category, useCases: useCasesPayload }
            : null;
        })
        .filter(Boolean);

      if (!selections.length) {
        message.warning("No selections to save");
        return;
      }

      const payload = {
        username: user?.username,
        userId: user?.code,
        organizationId,
        projectName: projectname,
        mainCategory,
        selections
      };

      await axios.post("/save-category", payload);

      message.success("All selected use cases saved successfully");
      await fetchProjectProcess();

    } catch (err) {
      console.error(err);
      message.error("Failed to save construction configuration");
    } finally {
      setSaving(false);
      setShowModal(false);
    }
  };

  const getCategoryIcon = (category) => {
    const firstUseCase = useCases?.[category]?.[0];
    return firstUseCase?.icon || ShieldCheck;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <Header auth={citriot?.auth} />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <Breadcrumb
          className="mb-4"
          items={[
            { title: <a onClick={() => navigate("/home")}>Sectors</a> },
            { title: mainCategory },
          ]}
        />

        {/* Header Card */}
        <Card
          bordered={false}
          className="mb-6 shadow-sm"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 12,
          }}
        >
          <Title level={2} className="!text-white !mb-2">
            Configure {mainCategory} Use Cases
          </Title>
          <Text className="text-blue-100">
            Select the {mainCategory.toLowerCase()} features required for your project
          </Text>
        </Card>

        {/* Main Table Card */}
        <Card bordered={false} className="shadow-md" style={{ borderRadius: 12 }}>
          {Object.keys(filteredUseCases).length === 0 && searchText.trim() ? (
            <div className="text-center py-10">
              <Title level={4}>No matching use cases found</Title>
              <Text type="secondary">
                Try different words or clear the search to see all options
              </Text>
            </div>
          ) : (
            <Table
              pagination={false}
              dataSource={tableData}
              rowKey="key"
              defaultExpandedRowKeys={tableData.map(item => item.key)}
              columns={[
                {
                  title: "Category",
                  dataIndex: "category",
                  render: (text) => {
                    const Icon = getCategoryIcon(text);
                    return (
                      <Space>
                        <Icon size={20} className="text-blue-500" />
                        <span className="font-medium">{text}</span>
                      </Space>
                    );
                  },
                },
                {
                  title: "Available Use Cases",
                  dataIndex: "count",
                  render: (count) => (
                    <Tag color="blue" className="px-3 py-1 rounded-full">
                      {count} Use Case{count !== 1 ? "s" : ""}
                    </Tag>
                  ),
                },
              ]}
              expandable={{
                expandRowByClick: true,
                expandedRowRender: (record) => (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filteredUseCases[record.category].map((useCase) => {
                        const Icon = useCase.icon || ShieldCheck;
                        const selected =
                          selectedData?.[record.category]?.[useCase.id] || [];

                        return (
                          <Card
                            key={useCase.id}
                            size="small"
                            className="shadow-sm"
                            style={{
                              borderRadius: 8, // ✅ removed premium border
                            }}
                          >
                            {/* HEADER */}
                            <div className="flex items-start gap-3 mb-3">
                              <div className="p-2 rounded-lg bg-blue-100">
                                <Icon size={20} className="text-blue-600" />
                              </div>

                              <div className="flex-1">
                                <div className="flex justify-between items-center">
                                  <Text strong>{useCase.title}</Text>

                                  {selected.length > 0 && (
                                    <CheckCircle2 size={18} className="text-green-500" />
                                  )}
                                </div>

                                <Text type="secondary" className="text-xs">
                                  {selected.length} of {useCase.functionalities.length} selected
                                </Text>
                              </div>
                            </div>

                            {/* ✅ FUNCTIONALITIES (PER ITEM PREMIUM) */}
                            <div className="flex flex-col gap-2">
                              {useCase.functionalities.map((f) => {
                                const value =
                                  typeof f === "string" ? f : f.name;

                                const isPremium =
                                  typeof f === "object" ? f.isPremium : false;

                                const checked = selected.includes(value);

                                return (
                                  <div
                                    key={value}
                                    className="flex justify-between items-center p-2 rounded-md hover:bg-gray-50"
                                  >
                                    <Checkbox
                                      checked={checked}
                                      onChange={(e) => {
                                        const newValues = e.target.checked
                                          ? [...selected, value]
                                          : selected.filter((v) => v !== value);

                                        onFunctionalityChange(
                                          record.category,
                                          useCase.id,
                                          newValues
                                        );
                                      }}
                                    >
                                      <span>{value}</span>
                                    </Checkbox>

                                    {/* ✅ PREMIUM BADGE PER FUNCTIONALITY */}
                                    {isPremium && (
                                      <Tag
                                        color="gold"
                                        icon={<CrownOutlined />}
                                        style={{
                                          borderRadius: 999,
                                          fontSize: 11,
                                        }}
                                      >
                                        Premium
                                      </Tag>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ),
                expandIcon: ({ expanded, onExpand, record }) => (
                  <Button
                    type="text"
                    size="small"
                    onClick={(e) => onExpand(record, e)}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    {expanded ? "Collapse" : "Expand"}
                  </Button>
                ),
              }}
            />
          )}

          {/* Footer */}
          <div className="mt-6 pt-6 border-t flex justify-between items-center">
            <Text>
              <CheckCircle2
                size={18}
                className="inline mr-2 text-green-500"
              />
              {selectedCount} feature
              {selectedCount !== 1 && "s"} selected
            </Text>

            <Button
              type="primary"
              size="large"
              disabled={selectedCount === 0}
              onClick={handleContinue}
              style={{
                background:
                  selectedCount > 0
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : undefined,
              }}
            >
              Continue ({selectedCount})
            </Button>
          </div>
        </Card>
      </div>

      {/* Confirmation Modal */}
      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save & Continue"
        width={600}
        title="Confirm mainCategory Selection"
      >
        {Object.entries(selectedData)
          .filter(([_, cases]) =>
            Object.values(cases).some((funcs) => funcs.length)
          )
          .map(([cat, cases]) => (
            <div key={cat}>
              <Divider>{cat}</Divider>

              {Object.entries(cases).map(([id, funcs]) =>
                funcs.length ? (
                  <div key={id} className="mb-2">
                    <strong>
                      {useCases?.[cat]?.find?.((u) => u.id === id)?.title || id}
                    </strong>
                    : {funcs.join(", ")}
                  </div>
                ) : null
              )}
            </div>
          ))}
      </Modal>
    </div>
  );
};

export default SectorUseCases;